import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { errorText } from '../lib/errors';
import { ErrorNote } from './ui';
import { Turnstile, TURNSTILE_SITE_KEY } from './Turnstile';

const RESEND_SECONDS = 60;
export const isEmail = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());

/**
 * Email → 6-digit code → signed in. Used by sign-in and by join links.
 * `head` renders above the email field, then `alt` (another way in, such as Google); `onSignedIn` runs after the code
 * is verified.
 */
export function EmailCode({ head, alt, codeHead, submitLabel = 'Email me a code', onSignedIn, footer }: {
  head: ReactNode;
  alt?: ReactNode;
  codeHead?: ReactNode;
  submitLabel?: string;
  onSignedIn: () => Promise<void> | void;
  footer?: ReactNode;
}) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const addr = email.trim().toLowerCase();

  async function sendCode() {
    setError(null);
    if (!isEmail(addr)) return setError('Enter your email address.');
    if (TURNSTILE_SITE_KEY && !captcha) return setError('Wait for the bot check to finish, then try again.');
    setBusy(true);
    const { error: e } = await supabase.auth.signInWithOtp({
      email: addr,
      options: { shouldCreateUser: true, ...(captcha ? { captchaToken: captcha } : {}) },
    });
    setBusy(false);
    setCaptchaReset((n) => n + 1);
    if (e) return setError(errorText(e));
    setCode('');
    setStep('code');
    setResendIn(RESEND_SECONDS);
  }

  async function verify() {
    setError(null);
    if (!/^\d{6}$/.test(code)) return setError('Enter all 6 digits of the code.');
    setBusy(true);
    const { error: e } = await supabase.auth.verifyOtp({ email: addr, token: code, type: 'email' });
    if (e) {
      setBusy(false);
      return setError(errorText(e));
    }
    try {
      await onSignedIn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'email') {
    return (
      <form className="auth" onSubmit={(e) => { e.preventDefault(); void sendCode(); }}>
        {head}
        {alt}
        <div>
          <label className="fld-l" htmlFor="email">Email</label>
          <input id="email" className="fld" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <Turnstile onToken={setCaptcha} resetKey={captchaReset} />
        <ErrorNote error={error} />
        <button className="btn primary block lg" type="submit" disabled={busy}>{busy ? 'Sending…' : submitLabel}</button>
        {footer}
      </form>
    );
  }

  return (
    <form className="auth" onSubmit={(e) => { e.preventDefault(); void verify(); }}>
      <button type="button" className="link-btn" onClick={() => { setStep('email'); setError(null); }}>‹ Change email</button>
      {codeHead}
      <h1>Enter the code</h1>
      <p className="muted">We emailed a 6-digit code to <b>{addr}</b>. It can take a minute; check Spam or Promotions too.</p>
      <div>
        <label className="fld-l" htmlFor="otp">6-digit code</label>
        <input id="otp" className="otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="······"
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
      </div>
      <ErrorNote error={error} />
      <button className="btn primary block lg" type="submit" disabled={busy}>{busy ? 'Checking…' : 'Verify and continue'}</button>
      {resendIn > 0 ? (
        <p className="fine">You can ask for a new code in {resendIn} s.</p>
      ) : (
        <>
          <Turnstile onToken={setCaptcha} resetKey={captchaReset} />
          <button type="button" className="link-btn" disabled={busy} onClick={() => void sendCode()}>Send a new code</button>
        </>
      )}
    </form>
  );
}
